/**
 * MBA Lab — value bucketing for analytics.
 *
 * Pure and dependency-free so it can be unit-tested, and exposed on window.MbaLabAnalytics.buckets
 * so wwwroot/js/college-search.js can use the same implementation. That file sits outside webpack
 * and cannot import, and a second copy of these rules would drift silently - the kind of drift that
 * produces two subtly different definitions of the same chart.
 *
 * Everything here exists to keep GA4 dimension cardinality low. A dimension that exceeds roughly
 * 500 unique values in a day collapses the remainder into a permanently undrillable "(other)" row,
 * and it degrades reports that do not even use that dimension. Raw search queries, raw result
 * counts, and raw link domains would each blow straight through that.
 *
 * Bucket boundaries are frozen once shipped. Rebucketing in place is worse than useless: the chart
 * keeps rendering, the trend line silently changes meaning, and nothing marks the seam. Mint a new
 * param name instead.
 */

/**
 * Length of what someone typed, never the text itself.
 *
 * The text is deliberately never sent - not raw, not hashed. Search Console already provides real
 * queries, unhashed and uncapped, so a hash here would add re-identification risk for information
 * we can already get properly.
 */
export function queryLengthBucket(length) {
  const n = Number(length) || 0;
  if (n <= 2) return '1-2';
  if (n <= 5) return '3-5';
  if (n <= 10) return '6-10';
  return '11+';
}

/** How many results a search produced. Zero is its own bucket - it is the actionable one. */
export function resultCountBucket(count) {
  const n = Number(count) || 0;
  if (n <= 0) return '0';
  if (n === 1) return '1';
  if (n <= 5) return '2-5';
  return '6+';
}

/**
 * How many deliverables the landing page's parser demo found in a paste. Its own param
 * (found_count_bucket), not result_count_bucket: that one's frozen meaning is search results,
 * and a full syllabus paste lands well above a search's typical counts - hence the finer top
 * end. Zero is the actionable bucket here too: it means the demo met a syllabus shape the
 * parser doesn't read, which is exactly the corpus-gap signal worth counting.
 */
export function parsedCountBucket(count) {
  const n = Number(count) || 0;
  if (n <= 0) return '0';
  if (n <= 2) return '1-2';
  if (n <= 5) return '3-5';
  if (n <= 10) return '6-10';
  return '11+';
}

/**
 * Why a search found nothing. Three values, each implying a different fix:
 *
 *  - `state_only`      they typed a state, not a school. We have state hubs; route them there.
 *  - `category_excluded` we know the school, but it has no page in this category.
 *  - `unknown_school`  we genuinely do not cover it. This is the content-gap signal.
 *
 * @param {boolean} matchedState  query matches a state name present in the index
 * @param {boolean} matchedButUnrenderable  results matched but none had a path for this category
 */
export function gapKind(matchedState, matchedButUnrenderable) {
  if (matchedState) return 'state_only';
  if (matchedButUnrenderable) return 'category_excluded';
  return 'unknown_school';
}

/**
 * Coarse classification of an outbound link.
 *
 * An explicit data-link-kind wins, because some kinds cannot be told apart from the host: a school's
 * Net Price Calculator usually lives on the school's own domain, so host sniffing alone would file
 * it as school_site and lose the highest-intent click on the page.
 *
 * The raw hostname rides along separately as link_domain. That one is deliberately NOT registered as
 * a GA4 custom dimension - 366 schools and growing would breach the cardinality ceiling - but it
 * still lands in BigQuery's event_params, where there is no such limit.
 */
export function linkKind(hostname, explicit) {
  if (explicit) return String(explicit);

  const host = String(hostname || '').toLowerCase();
  if (!host) return 'other';
  if (host === 'collegescorecard.ed.gov' || host.endsWith('.collegescorecard.ed.gov')) return 'scorecard';
  if (host === 'aacsb.edu' || host.endsWith('.aacsb.edu')) return 'accreditation';
  if (host.endsWith('.edu')) return 'school_site';
  return 'other';
}

/**
 * Whether a link leaves this site. Protocol-relative and same-host links are internal; anything on
 * another host is outbound, including subdomains, which are separately operated in practice.
 */
export function isOutbound(linkHost, currentHost) {
  if (!linkHost) return false;
  return String(linkHost).toLowerCase() !== String(currentHost || '').toLowerCase();
}
